import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";

import { SiteFooter, SiteHeader } from "../components/site-header";
import { seo } from "../lib/head";
import { HELP_SECTIONS, helpJsonLd } from "../lib/help";
import { LEGAL } from "../lib/legal";

export const Route = createFileRoute("/aide")({
  staticData: { ssr: true },
  head: () =>
    seo({
      title: "Aide · Comment fonctionne une liste Listou",
      description:
        "Réserver un cadeau sans compte, le faire livrer, rester anonyme, annuler ; créer une liste, garder la surprise et découvrir qui a offert quoi.",
      jsonLd: helpJsonLd(),
    }),
  component: HelpPage,
});

function HelpPage() {
  // `<details>` does not open itself for a `#anchor`: a link to /aide#annuler should land
  // on the answer, unfolded.
  useEffect(() => {
    const open = () => {
      const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (target instanceof HTMLDetailsElement) {
        target.open = true;
        target.scrollIntoView({ block: "start" });
      }
    };
    open();
    window.addEventListener("hashchange", open);
    return () => window.removeEventListener("hashchange", open);
  }, []);

  return (
    <div className="min-h-dvh">
      <SiteHeader signedIn={false} />
      <main className="mx-auto grid max-w-3xl gap-12 px-4 py-12 sm:px-6">
        <header className="grid gap-4">
          <h1 className="text-4xl font-semibold sm:text-5xl">Comment ça marche ?</h1>
          <p className="max-w-[52ch] text-lg text-muted">
            Vous offrez un cadeau ou vous préparez votre liste : les réponses sont ici.
          </p>
          <nav aria-label="Rubriques" className="flex flex-wrap gap-2">
            {HELP_SECTIONS.map((section) => (
              <a key={section.id} href={`#${section.id}`} className="btn-secondary btn-sm">
                {section.title}
              </a>
            ))}
          </nav>
        </header>

        {HELP_SECTIONS.map((section) => (
          <section key={section.id} id={section.id} className="grid scroll-mt-6 gap-4">
            <header className="grid gap-1">
              <h2 className="text-3xl font-semibold">{section.title}</h2>
              <p className="text-muted">{section.intro}</p>
            </header>
            <div className="grid gap-3">
              {section.questions.map((item) => (
                <details
                  key={item.id}
                  id={item.id}
                  className="panel group scroll-mt-6 open:shadow-sm"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 text-lg font-bold [&::-webkit-details-marker]:hidden">
                    {item.question}
                    <span
                      aria-hidden
                      className="grid size-8 shrink-0 place-items-center rounded-full bg-ink/[0.06] text-xl leading-none transition group-open:rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <div className="grid gap-3 px-5 pb-5 leading-relaxed text-ink/85">
                    {item.answer.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </section>
        ))}

        <section className="panel grid gap-3 p-6 text-center">
          <h2 className="text-2xl font-semibold">Pas trouvé votre réponse ?</h2>
          <p className="text-muted">
            Écrivez-nous à{" "}
            <a className="font-bold text-ink underline" href={`mailto:${LEGAL.contactEmail}`}>
              {LEGAL.contactEmail}
            </a>
            , nous répondons vite.
          </p>
          <Link to="/" className="btn justify-self-center">
            Créer ma liste
          </Link>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
